import { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  School,
  Notifications,
  Warning,
  ContactMail,
  Payment,
  People,
  QuestionAnswer,
  PhotoLibrary,
  Feedback,
  Description,
  Label,
  Logout,
} from '@mui/icons-material';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface Feedback {
  id: string;
  isRead?: boolean;
}

const menuItems = [
  { text: 'ダッシュボード', icon: <Dashboard />, path: '/dashboard' },
  { text: '教室一覧', icon: <School />, path: '/schools' },
  { text: 'お知らせ', icon: <Notifications />, path: '/notifications' },
  { text: '重要なお知らせ', icon: <Warning />, path: '/important-notices' },
  { text: 'お問い合わせ', icon: <ContactMail />, path: '/inquiries' },
  { text: 'コンテンツ管理', icon: <Description />, path: '/content' },
  { text: 'Stripe管理', icon: <Payment />, path: '/stripe' },
  { text: 'ユーザー管理', icon: <People />, path: '/users' },
  { text: 'FAQ管理', icon: <QuestionAnswer />, path: '/faqs' },
  { text: 'FAQカテゴリ管理', icon: <Label />, path: '/faq-categories' },
  { text: 'メディアライブラリ', icon: <PhotoLibrary />, path: '/media-library' },
  { text: 'フィードバック', icon: <Feedback />, path: '/feedbacks', showBadge: true },
];

export default function DashboardLayout() {
  const [open, setOpen] = useState(true);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();

  // すべてのフィードバックを取得してクライアント側でフィルタリング
  const { data: allFeedbacks, loading: feedbacksLoading } = useCollection<Feedback>(
    'feedback',
    (q) => fbQuery(q, fbOrderBy('createdAt', 'desc'))
  );

  // 未読フィードバック数を計算
  const unreadFeedbacks = allFeedbacks?.filter(feedback => !feedback.isRead) || [];
  const unreadCount = unreadFeedbacks.length;

  // デバッグ用ログ
  console.log('All feedbacks:', allFeedbacks);
  console.log('Unread feedbacks:', unreadFeedbacks);
  console.log('Unread count:', unreadCount);
  console.log('Loading:', feedbacksLoading);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            LessonMate 管理画面
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.email}
          </Typography>
          <IconButton color="inherit" onClick={signOut}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton onClick={() => navigate(item.path)}>
                  <ListItemIcon>
                    {item.showBadge ? (
                      <Badge 
                        badgeContent={unreadCount} 
                        color="error"
                        invisible={unreadCount === 0}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
        </Box>
      </Drawer>

      <Main open={open}>
        <Toolbar />
        <Outlet />
      </Main>
    </Box>
  );
}